import axios from "axios";
import { logActivity } from "../utils/activity";

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

if (!apiBaseUrl) {
  throw new Error("REACT_APP_API_BASE_URL is not defined");
}

if (
  process.env.NODE_ENV === "production" &&
  !apiBaseUrl.startsWith("https://")
) {
  console.warn("⚠️ Production API is not using HTTPS");
}

const normalizedBaseUrl = apiBaseUrl.replace(/\/+$/, "");
const apiRoot = normalizedBaseUrl.endsWith("/api/v1")
  ? normalizedBaseUrl
  : `${normalizedBaseUrl}/api/v1`;

const api = axios.create({
  baseURL: apiRoot,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

const TOKEN_KEY = "myv_token";
const TOKEN_EXPIRES_KEY = "myv_token_expires_at";
const SESSION_KEY = "myv_session";
const PENDING_KEY = "myv_pending";
const SESSION_ACTIVE_KEY = "myv_session_active";
const PLATFORM_CONTEXT_KEY = "myv_platform_context";

const readStorageValue = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw);
    } catch (parseError) {
      return raw;
    }
  } catch (error) {
    return null;
  }
};

const PUBLIC_API_PREFIXES = ["/Auth/"];

const isPublicEndpoint = (url) =>
  PUBLIC_API_PREFIXES.some((prefix) => url?.startsWith(prefix));

const UNAUTHORIZED_MARKERS = [
  "unauthorized",
  "access_denied",
  "auth_required",
  "permission denied",
  "auth_token_invalid",
];

const hasUnauthorizedMarker = (value) => {
  if (!value) return false;
  const normalized = String(value).toLowerCase();
  return UNAUTHORIZED_MARKERS.some((marker) => normalized.includes(marker));
};

const isUnauthorizedPayload = (payload) =>
  hasUnauthorizedMarker(payload?.code) ||
  hasUnauthorizedMarker(payload?.message) ||
  hasUnauthorizedMarker(payload?.status);

const normalizeResponse = (response) => {
  const payload = response?.data;
  const isRaw = response?.config?.meta?.rawResponse;
  if (isRaw) {
    return payload ?? null;
  }
  if (payload?.code === "AUTH_TOKEN_INVALID" || isUnauthorizedPayload(payload)) {
    const error = new Error(payload.message || "Authentication required");
    error.response = response;
    error.code = payload?.code || "AUTH_REQUIRED";
    error.isAuthRequired = true;
    throw error;
  }
  if (payload?.status && payload.status !== "success") {
    const error = new Error(payload.message || "API error");
    error.response = response;
    throw error;
  }
  return payload?.data ?? null;
};

const clearSession = () => {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXPIRES_KEY);
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(PENDING_KEY);
    localStorage.removeItem(SESSION_ACTIVE_KEY);
    localStorage.removeItem(PLATFORM_CONTEXT_KEY);
    sessionStorage.removeItem(PLATFORM_CONTEXT_KEY);
  } catch (error) {
    // no-op
  }
};

api.interceptors.request.use((config) => {
  const token = readStorageValue(TOKEN_KEY);
  const expiresAt = readStorageValue(TOKEN_EXPIRES_KEY);
  const isExpired = typeof expiresAt === "number" && Date.now() > expiresAt;
  const url = config?.url || "";
  if (!isPublicEndpoint(url) && (!token || isExpired)) {
    const error = new Error("AUTH_REQUIRED");
    error.code = "AUTH_REQUIRED";
    error.isAuthRequired = true;
    return Promise.reject(error);
  }
  if (!isPublicEndpoint(url) && token && !isExpired) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  logActivity("api_call", { method: config.method, url: config.url });
  return config;
});

api.interceptors.response.use(
  (response) => normalizeResponse(response),
  (error) => {
    const isRaw = error?.config?.meta?.rawResponse;
    if (isRaw && error?.response?.data) {
      return Promise.resolve(error.response.data);
    }
    const status = error?.response?.status;
    const code = error?.response?.data?.code || error?.code;
    if (process.env.NODE_ENV !== "production") {
      console.error("API error", {
        status,
        code,
        url: error?.config?.url,
        message: error?.response?.data?.message || error?.message,
      });
    }
    if (status === 403) {
      logActivity("api_error", { status, reason: "forbidden" });
      return Promise.resolve(null);
    }
    if (status === 401 || status === 403 || isUnauthorizedPayload(error?.response?.data) || hasUnauthorizedMarker(code)) {
      error.isAuthRequired = true;
      logActivity("api_error", { status, reason: "unauthorized" });
    } else {
      logActivity("api_error", { status: status || "unknown" });
    }
    return Promise.reject(error);
  }
);

export const isUnauthorizedError = (error) => {
  if (!error) return false;
  if (error.isAuthRequired) return true;
  const status = error?.response?.status;
  const code = error?.response?.data?.code || error?.code;
  const message = error?.response?.data?.message || error?.message;
  return (
    status === 401 ||
    status === 403 ||
    hasUnauthorizedMarker(code) ||
    hasUnauthorizedMarker(message)
  );
};

export default api;
